import React from 'react';
import './IncomeStatementTable.css';

const FIELD_LABELS = {
  date: 'Statement Date',
  reportedCurrency: 'Reported Currency',
  filingDate: 'Filing Date',
  acceptedDate: 'Accepted Date',
  period: 'Period',
  revenue: 'Revenue',
  costOfRevenue: 'Cost of Revenue',
  grossProfit: 'Gross Profit',
  researchAndDevelopmentExpenses: 'R&D Expenses',
  generalAndAdministrativeExpenses: 'General & Administrative Expenses',
  sellingAndMarketingExpenses: 'Selling & Marketing Expenses',
  sellingGeneralAndAdministrativeExpenses: 'SG&A Expenses',
  otherExpenses: 'Other Expenses',
  operatingExpenses: 'Operating Expenses',
  costAndExpenses: 'Cost and Expenses',
  netInterestIncome: 'Net Interest Income',
  interestIncome: 'Interest Income',
  interestExpense: 'Interest Expense',
  depreciationAndAmortization: 'Depreciation & Amortization',
  ebitda: 'EBITDA',
  ebit: 'EBIT',
  nonOperatingIncomeExcludingInterest: 'Non-Operating Income Ex. Interest',
  operatingIncome: 'Operating Income',
  totalOtherIncomeExpensesNet: 'Other Income/Expenses Net',
  incomeBeforeTax: 'Income Before Tax',
  incomeTaxExpense: 'Income Tax Expense',
  netIncomeFromContinuingOperations: 'Net Income From Continuing Ops',
  netIncomeFromDiscontinuedOperations: 'Net Income From Discontinued Ops',
  otherAdjustmentsToNetIncome: 'Other Adjustments To Net Income',
  netIncome: 'Net Income',
  netIncomeDeductions: 'Net Income Deductions',
  bottomLineNetIncome: 'Bottom Line Net Income',
  eps: 'EPS',
  epsDiluted: 'Diluted EPS',
  weightedAverageShsOut: 'Weighted Average Shares Out',
  weightedAverageShsOutDil: 'Weighted Average Shares Out Diluted'
};

const FIELD_ORDER = [
  'date',
  'reportedCurrency',
  'filingDate',
  'acceptedDate',
  'period',
  'revenue',
  'costOfRevenue',
  'grossProfit',
  'researchAndDevelopmentExpenses',
  'generalAndAdministrativeExpenses',
  'sellingAndMarketingExpenses',
  'sellingGeneralAndAdministrativeExpenses',
  'otherExpenses',
  'operatingExpenses',
  'costAndExpenses',
  'netInterestIncome',
  'interestIncome',
  'interestExpense',
  'depreciationAndAmortization',
  'ebitda',
  'ebit',
  'nonOperatingIncomeExcludingInterest',
  'operatingIncome',
  'totalOtherIncomeExpensesNet',
  'incomeBeforeTax',
  'incomeTaxExpense',
  'netIncomeFromContinuingOperations',
  'netIncomeFromDiscontinuedOperations',
  'otherAdjustmentsToNetIncome',
  'netIncome',
  'netIncomeDeductions',
  'bottomLineNetIncome',
  'eps',
  'epsDiluted',
  'weightedAverageShsOut',
  'weightedAverageShsOutDil'
];

const CURRENCY_FIELDS = new Set([
  'revenue',
  'costOfRevenue',
  'grossProfit',
  'researchAndDevelopmentExpenses',
  'generalAndAdministrativeExpenses',
  'sellingAndMarketingExpenses',
  'sellingGeneralAndAdministrativeExpenses',
  'otherExpenses',
  'operatingExpenses',
  'costAndExpenses',
  'netInterestIncome',
  'interestIncome',
  'interestExpense',
  'depreciationAndAmortization',
  'ebitda',
  'ebit',
  'nonOperatingIncomeExcludingInterest',
  'operatingIncome',
  'totalOtherIncomeExpensesNet',
  'incomeBeforeTax',
  'incomeTaxExpense',
  'netIncomeFromContinuingOperations',
  'netIncomeFromDiscontinuedOperations',
  'otherAdjustmentsToNetIncome',
  'netIncome',
  'netIncomeDeductions',
  'bottomLineNetIncome'
]);

const SHARE_FIELDS = new Set([
  'weightedAverageShsOut',
  'weightedAverageShsOutDil'
]);

const DECIMAL_FIELDS = new Set(['eps', 'epsDiluted']);

const DATE_FIELDS = new Set(['date', 'filingDate', 'acceptedDate']);

function formatValue(field, value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (DATE_FIELDS.has(field)) {
    return String(value);
  }

  if (CURRENCY_FIELDS.has(field) && typeof value === 'number') {
    return `${value < 0 ? '-$' : '$'}${Math.abs(value).toLocaleString('en-US')}`;
  }

  if (DECIMAL_FIELDS.has(field) && typeof value === 'number') {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (SHARE_FIELDS.has(field) && typeof value === 'number') {
    return value.toLocaleString('en-US');
  }

  if (typeof value === 'number') {
    return value.toLocaleString('en-US');
  }

  return String(value);
}

export default function IncomeStatementTable({ rows = [], selectedStock }) {
  if (!rows.length) {
    return null;
  }

  const sortedRows = [...rows].sort((a, b) => Number(a.fiscalYear) - Number(b.fiscalYear));
  const years = sortedRows.map((row) => row.fiscalYear);

  return (
    <section className="income-statement-table-card">
      <div className="income-statement-table-header">
        <div>
          <h3 className="income-statement-table-title">{selectedStock} Income Statement Table</h3>
          <p className="income-statement-table-subtitle">Full values, grouped by metric and fiscal year.</p>
        </div>
      </div>

      <div className="income-statement-table-scroll">
        <table className="income-statement-table">
          <thead>
            <tr>
              <th className="income-statement-metric-column">Metric</th>
              {years.map((year) => (
                <th key={year}>{year}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FIELD_ORDER.map((field) => (
              <tr key={field}>
                <td className="income-statement-metric-cell">
                  {FIELD_LABELS[field] || field}
                </td>
                {sortedRows.map((row) => (
                  <td key={`${field}-${row.fiscalYear}`} className="income-statement-value-cell">
                    {formatValue(field, row[field])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
